<script setup lang="ts">
import type { SolrParams } from '#shared/solr'
import type { JournalListItem } from '~/utils/solr/journal'
import { SolrParamsKey } from '#shared/solr'
import IssueSection from '~/components/journals/IssueSection.vue'
import PublisherHeader from '~/components/journals/PublisherHeader.vue'
import VolumeFilter from '~/components/journals/VolumeFilter.vue'
import { useTrackLastActivity } from '~/composables/useLastActivity'

const route = useRoute<'journal-publication'>()
const offset = useHeaderHeight()
const { $sanitizeHtml } = useNuxtApp()
const publisherId = computed(() => {
  const params = route.params as { publisherId?: string | string[] }
  const param = params.publisherId
  return Array.isArray(param) ? param.join('/') : String(param ?? '')
})

const publisherQuery = computed<SolrParams>(() => ({
  [SolrParamsKey.PublisherId]: publisherId.value,
  [SolrParamsKey.Sort]: 'year desc',
} as SolrParams))

const { data, isLoading, error } = useSolrQueryV2(publisherQuery, 'journal', 'journals')

const issues = computed(() => data.value?.response?.docs ?? [] as JournalListItem[])
const firstIssue = computed(() => issues.value?.[0])
const journalProfile = computed(() => {
  const response = data.value?.response
  if (!response || !('journalProfile' in response)) {
    return null
  }

  return $sanitizeHtml(response.journalProfile ?? null)
})
const publisher = computed(() => ({
  title: firstIssue.value?.title,
  publisherId: firstIssue.value?.publisherId,
}))

const volumes = computed<string[]>(() => {
  const set = new Set<string>()
  for (const issue of issues.value) {
    const v = issue.volume?.toString()?.trim()
    if (v) {
      set.add(v)
    }
  }
  return Array.from(set).sort((a, b) => Number(b) - Number(a))
})

const selectedVolume = ref<string | null>(null)
const filteredIssues = computed(() => {
  const list = selectedVolume.value ? issues.value.filter(i => i.volume?.toString() === selectedVolume.value?.toString()) : issues.value

  return [...list].sort((a, b) => {
    const volumeDiff = Number(b.volume) - Number(a.volume)
    if (volumeDiff !== 0) {
      return volumeDiff
    }
    return Number(b.issue) - Number(a.issue)
  })
})

const title = computed(() => publisher.value?.title ?? 'Forlegger')

useTrackLastActivity(title)

definePageMeta({
  name: 'journal-publication',
  layout: 'default',
  colorMode: 'light',
  theme: 'carbon',
})

useHead({
  title,
  bodyAttrs: { class: 'bg-white' },
})
</script>

<template>
  <div>
    <ContentDiv class="flex items-start">
      <Breadcrumbs />
    </ContentDiv>

    <ContentDiv>
      <div v-if="error || isLoading" class="mt-l">
        <Skeleton v-if="isLoading" />
        <StatusMessage v-else status="error" title="Kunne ikke laste tidsskrift" />
      </div>

      <div v-else-if="publisher" class="flex flex-col min-[900px]:flex-row gap-m lg:gap-l xl:gap-3xl max-w-screen-xl mx-auto py-xl">
        <div class="relative mx-auto min-[900px]:mx-0 shrink-0">
          <PublisherHeader
            data-sticky-publisher-header
            class="sticky"
            :title="publisher.title || ''"
            :cover-image="firstIssue?.coverImage || undefined"
          />
        </div>

        <div class="flex-1 flex flex-col gap-xl pb-2xl">
          <h1 class="title-xl">
            {{ publisher.title }}
          </h1>
          <BodyText v-if="journalProfile" :html="journalProfile" class="journal-profile" />
          <VolumeFilter v-if="volumes.length" id="journal-volume" v-model="selectedVolume" :volumes="volumes" />
          <IssueSection v-for="issue in filteredIssues" :key="issue.id" :issue="issue" />
        </div>
      </div>
    </ContentDiv>
  </div>
</template>

<style scoped lang="postcss">
[data-sticky-publisher-header] {
  top: calc(v-bind(offset) + theme('spacing.s'));
}

:deep(.journal-profile p:first-of-type) {
  @apply text-xl font-light leading-8 mb-8 font-heading;
}

:deep(.journal-profile p:last-child) {
  @apply mb-0;
}
</style>
